import { useMemo, useState } from 'react';
import { bytes } from '../../lib/n4';
import { ApiError, api } from '../../lib/api';
import { type ChangeSet, type CheckRow, type FileChange, type Site, changeSetOf, checkRows, errText, failOf, healthOf, noteOf, post, useProjects } from './common';

// Kiadások fül, „Kiadás innen”: forrásmappa + kizárások → Száraz próba (diff, nem vált) / Kiadás (ellenőrzéssel, auto-rollbackkel).

// Kiadás / ellenőrzés hívása: az ok:false válasz csak akkor kivétel, ha nincs benne eredmény (rollback, checks) — azt meg kell mutatni
export async function resultCall(siteId: number, path: string, body: unknown): Promise<Record<string, unknown>> {
  const r = await api<Record<string, unknown>>(`/api/n4/sites/${siteId}${path}`, post(body));
  if (r && typeof r === 'object' && !('rolled_back' in r) && !('checks' in r) && !('changes' in r)) failOf(r);
  return r ?? {};
}

const CHECK_KIND: Record<CheckRow['kind'], string> = { health: 'health', header: 'fejléc', smoke: 'smoke' };
const okMark = (ok: boolean | null) => (ok === true ? <span className="okc">OK</span> : ok === false ? <span className="badc">HIBA</span> : <span className="mut">?</span>);

// Ellenőrzés-táblázat (kiadás utáni és „Ellenőrzés most”): health_paths, required_headers, smoke egy táblában
export function ChecksTable({ rows }: { rows: CheckRow[] }) {
  if (!rows.length) return <p className="a-site-note">Nincs ellenőrzés-eredmény (nincs beállítva, vagy a szerver nem adott vissza).</p>;
  const bad = rows.filter((r) => r.ok === false).length;
  return (
    <div className="a-site-tablewrap">
      <table className="a-site-table a-site-checks">
        <thead><tr><th>Fajta</th><th>Útvonal</th><th>Mit</th><th className="num">Státusz</th><th className="c">Eredmény</th><th>Részlet</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={r.ok === false ? 'badrow' : ''}>
              <td><span className={`a-site-ck k-${r.kind}`}>{CHECK_KIND[r.kind]}</span></td>
              <td className="mono">{r.path || <span className="mut">—</span>}</td>
              <td className="mono">{r.what || <span className="mut">—</span>}</td>
              <td className="num">{r.status || '—'}</td>
              <td className="c">{okMark(r.ok)}</td>
              <td className="sub2">{r.detail}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><td colSpan={6}>{rows.length} ellenőrzés · {bad ? <span className="badc">{bad} hibás</span> : <span className="okc">mind rendben</span>}</td></tr></tfoot>
      </table>
    </div>
  );
}

// Összesítő sor: +új ~változott −törölt · változatlan · bájt be/ki
export function ChangeSummary({ c }: { c: ChangeSet }) {
  return (
    <span className="a-site-chsum">
      <b className="add">+{c.n.added}</b> <b className="chg">~{c.n.changed}</b> <b className="del">−{c.n.removed}</b>
      {c.unchanged != null && <span> · {c.unchanged} változatlan</span>}
      {(c.bytes_in != null || c.bytes_out != null) && <span> · be {bytes(c.bytes_in ?? 0)} / ki {bytes(c.bytes_out ?? 0)}</span>}
    </span>
  );
}

type Kind = 'added' | 'changed' | 'removed';
const KIND_TEXT: Record<Kind, string> = { added: 'új', changed: 'változott', removed: 'törölt' };
const MAX_ROWS = 300;
function DiffTable({ c }: { c: ChangeSet }) {
  const [only, setOnly] = useState<Kind | 'all'>('all');
  const rows = useMemo(() => {
    const all: (FileChange & { k: Kind })[] = [];
    for (const k of ['added', 'changed', 'removed'] as Kind[]) if (only === 'all' || only === k) for (const f of c[k]) all.push({ ...f, k });
    return all;
  }, [c, only]);
  return (
    <div className="a-site-diff">
      <div className="a-site-bar">
        <div className="a-site-seg">
          {(['all', 'added', 'changed', 'removed'] as const).map((k) => <button key={k} type="button" className={only === k ? 'on' : ''} onClick={() => setOnly(k)}>{k === 'all' ? `mind ${c.n.added + c.n.changed + c.n.removed}` : `${KIND_TEXT[k]} ${c.n[k]}`}</button>)}
        </div>
      </div>
      <div className="a-site-tablewrap a-site-difftw">
        <table className="a-site-table">
          <thead><tr><th>Változás</th><th>Útvonal</th><th className="num">Méret</th></tr></thead>
          <tbody>
            {rows.slice(0, MAX_ROWS).map((f, i) => (
              <tr key={`${f.k}:${f.path}:${i}`}>
                <td><span className={`a-site-dk ${f.k}`}>{KIND_TEXT[f.k]}</span></td>
                <td className="mono a-site-dpath">{f.path}</td>
                <td className="num">{f.size != null ? bytes(f.size) : '—'}{f.delta ? <div className="sub">{f.delta > 0 ? '+' : '−'}{bytes(Math.abs(f.delta))}</div> : null}</td>
              </tr>
            ))}
            {rows.length > MAX_ROWS && <tr className="empty"><td colSpan={3}>… és még {rows.length - MAX_ROWS} fájl</td></tr>}
            {rows.length === 0 && <tr className="empty"><td colSpan={3}>{c.n.added + c.n.changed + c.n.removed ? 'Ebben a szűrőben nincs fájl.' : 'Nincs eltérés: a forrás megegyezik a jelenlegi kiadással.'}</td></tr>}
          </tbody>
          <tfoot><tr><td colSpan={3}><ChangeSummary c={c} /></td></tr></tfoot>
        </table>
      </div>
    </div>
  );
}

interface DeployResult { release: string; changes: ChangeSet | null; checks: CheckRow[]; rolledBack: boolean; rollbackInfo: string; errors: string[]; health: ReturnType<typeof healthOf>; note: string }
function resultOf(r: unknown): DeployResult {
  const o = (r && typeof r === 'object' ? r : {}) as Record<string, unknown>;
  const rel = o.release;
  const release = rel == null ? '' : typeof rel === 'object' ? String((rel as Record<string, unknown>).id ?? (rel as Record<string, unknown>).dir ?? '') : String(rel);
  const rb = o.rolled_back;
  const rbObj = rb && typeof rb === 'object' ? (rb as Record<string, unknown>) : null;
  const errs = o.errors ?? o.failures ?? rbObj?.errors;
  return {
    release, changes: changeSetOf(o.changes), checks: checkRows(o),
    rolledBack: Boolean(rb), rollbackInfo: rbObj ? String(rbObj.to ?? rbObj.release ?? rbObj.release_id ?? '') : typeof rb === 'string' ? rb : '',
    errors: Array.isArray(errs) ? errs.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))) : typeof errs === 'string' ? [errs] : [],
    health: healthOf({ health: o.health }), note: noteOf(r),
  };
}

const LS_KEY = (id: number) => `n4site-deploy-${id}`;
function loadPrefs(site: Site): { from: string; exclude: string } {
  try { const v = JSON.parse(localStorage.getItem(LS_KEY(site.id)) || 'null') as { from?: string; exclude?: string } | null; if (v) return { from: v.from ?? '', exclude: v.exclude ?? '' }; } catch { /* nincs mentve */ }
  return { from: site.watch?.from ?? '', exclude: '' };
}

export function DeployPanel({ site, onDeployed }: { site: Site; onDeployed: () => void }) {
  const projects = useProjects();
  const [prefs] = useState(() => loadPrefs(site));
  const [from, setFrom] = useState(prefs.from);
  const [exclude, setExclude] = useState(prefs.exclude);
  const [note, setNote] = useState('');
  const [restart, setRestart] = useState(true);
  const [busy, setBusy] = useState<'dry' | 'deploy' | null>(null);
  const [dry, setDry] = useState<{ c: ChangeSet; note: string } | null>(null);
  const [result, setResult] = useState<DeployResult | null>(null);
  const [err, setErr] = useState('');
  const [refused, setRefused] = useState(''); // busy-őr (409): a „Mégis” gomb force-szal küldi

  const project = site.project_id != null ? projects.find((p) => p.id === site.project_id) : null;
  // A „from” a projekt mappájához képest; abszolút útvonal változatlanul megy
  const absFrom = (() => {
    const f = from.trim(); if (!f) return '';
    if (f.startsWith('/')) return f;
    if (!project?.dir) return '';
    return `${project.dir.replace(/\/+$/, '')}/${f.replace(/^\.\/+/, '')}`;
  })();
  const excludeList = exclude.split(',').map((x) => x.trim()).filter(Boolean);
  const fromErr = !from.trim() ? '' : !absFrom ? (site.project_id == null ? 'Nincs projekt: abszolút útvonal kell (/…).' : 'A projekt mappája még töltődik…') : '';
  const remember = () => { try { localStorage.setItem(LS_KEY(site.id), JSON.stringify({ from: from.trim(), exclude })); } catch { /* tele a tár */ } };

  const runDry = async () => {
    if (!absFrom) return;
    setBusy('dry'); setErr(''); setRefused(''); setResult(null);
    try {
      const r = await resultCall(site.id, '/deploy', { from: absFrom, exclude: excludeList, dry: true });
      const c = changeSetOf(r.changes ?? r);
      if (!c) throw new Error('a szerver nem adott változás-listát (a száraz próba még nem él a szerveren?)');
      setDry({ c, note: noteOf(r) }); remember();
    } catch (e) { setErr(errText(e, 'Száraz próba')); } finally { setBusy(null); }
  };
  const runDeploy = async (force = false) => {
    if (!absFrom) return;
    const sum = dry ? `\n\nA száraz próba szerint: +${dry.c.n.added} új, ~${dry.c.n.changed} változott, −${dry.c.n.removed} törölt.` : '';
    if (!force && !confirm(`Kiadod a weboldalt (${site.name}) innen?\n\n${absFrom}${excludeList.length ? `\nkivéve: ${excludeList.join(', ')}` : ''}${sum}\n\nUtána a szerver ellenőrzi a valódi címet${site.auto_rollback == null || site.auto_rollback ? ', hiba esetén visszaáll az előző kiadásra' : ''}.`)) return;
    setBusy('deploy'); setErr(''); setRefused('');
    try {
      const body: Record<string, unknown> = { from: absFrom, exclude: excludeList, restart };
      if (note.trim()) body.note = note.trim();
      if (force) body.force = true;
      const r = await resultCall(site.id, '/deploy', body);
      setResult(resultOf(r)); setDry(null); setNote(''); remember(); onDeployed();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setRefused(e.detail || e.code);
      else setErr(errText(e, 'Kiadás'));
    } finally { setBusy(null); }
  };

  return (
    <div className="a-site-deploy">
      <div className="a-site-sec">
        <h3>Kiadás innen</h3>
        <p className="a-site-note">{project ? <>A „from” a projekt mappájához képest: <code>{project.dir ?? project.name}</code>.</> : site.project_id == null ? 'Nincs projekt: a „from” abszolút útvonal.' : ''} A száraz próba nem vált, csak megmutatja, mi változna.</p>
        <div className="a-site-fields">
          <label className="a-site-field"><span>Forrás (from)</span><input className="a-input mono" value={from} placeholder="dist" onChange={(e) => { setFrom(e.target.value); setDry(null); }} />
            {fromErr ? <small className="bad">{fromErr}</small> : absFrom && absFrom !== from.trim() ? <small>→ {absFrom}</small> : null}</label>
          <label className="a-site-field"><span>Kivéve (vesszővel)</span><input className="a-input mono" value={exclude} placeholder="admin.html, *.map" onChange={(e) => { setExclude(e.target.value); setDry(null); }} /></label>
          <label className="a-site-field"><span>Megjegyzés</span><input className="a-input" value={note} placeholder="(nem kötelező)" onChange={(e) => setNote(e.target.value)} /></label>
        </div>
        <div className="a-site-rowbtns">
          <label className="a-site-check-inline big" title={site.kind === 'static' ? 'Statikus oldalnál nincs mit újraindítani' : 'Kiadás után a szolgáltatás újraindul'}><input type="checkbox" checked={restart} onChange={(e) => setRestart(e.target.checked)} />újraindítás</label>
          <button type="button" className="a-btn sm" disabled={busy != null || !absFrom} onClick={() => void runDry()}>{busy === 'dry' ? 'Száraz próba…' : 'Száraz próba'}</button>
          <button type="button" className="a-btn sm solid" disabled={busy != null || !absFrom} onClick={() => void runDeploy()}>{busy === 'deploy' ? 'Kiadás és ellenőrzés…' : 'Kiadás'}</button>
        </div>
        {err && <div className="a-site-msg bad">{err}</div>}
        {refused && (
          <div className="a-site-msg bad a-site-block">
            <span>Az oldal most foglalt (busy-őr): {refused}</span>
            <button type="button" className="a-btn sm" disabled={busy != null} onClick={() => { if (confirm('Mégis kiadod, a foglaltság ellenére? A folyamatban lévő munka (játszma, feltöltés…) megszakadhat.')) void runDeploy(true); }}>Mégis kiadom (force)</button>
          </div>
        )}
      </div>

      {dry && (
        <div className="a-site-result a-site-dryres">
          <div className="h"><b>Száraz próba</b><ChangeSummary c={dry.c} /><button type="button" onClick={() => setDry(null)} title="Bezárás">×</button></div>
          {dry.note && <p className="a-site-note">{dry.note}</p>}
          <DiffTable c={dry.c} />
        </div>
      )}

      {result && (
        <div className={`a-site-result ${result.rolledBack || result.checks.some((c) => c.ok === false) ? 'bad' : ''}`}>
          <div className="h"><b>{result.rolledBack ? 'Kiadás visszavonva' : 'Kiadás kész'}</b>{result.release && <code className="a-site-relid">{result.release}</code>}{result.changes && <ChangeSummary c={result.changes} />}<button type="button" onClick={() => setResult(null)} title="Bezárás">×</button></div>
          {result.rolledBack && (
            <div className="a-site-rbbar">
              <b>FIGYELEM: az ellenőrzés elbukott, visszaállítva az előző kiadásra{result.rollbackInfo ? ` (${result.rollbackInfo})` : ''}.</b>
              {result.errors.length > 0 && <ul>{result.errors.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            </div>
          )}
          {!result.rolledBack && result.errors.length > 0 && <ul className="a-site-warnlist">{result.errors.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          <div className="a-site-resmeta">
            {result.health.text && <span className={`hl ${result.health.state}`}>{result.health.text}</span>}
            {result.note && <span>{result.note}</span>}
          </div>
          <ChecksTable rows={result.checks} />
        </div>
      )}
    </div>
  );
}
